import { StatusBadge, SumitStatusBadge } from '@/components/common'
import type { SumitStatus } from '@/components/common'
import type { Document } from '@/types'
import { getDocumentTypeLabel } from '@/types'
import { getSumitDocumentUrl } from '@/utils'

interface DocumentRowProps {
  document: Document
  onSelect: () => void
  onDownload: () => void
}

// Format currency value
function formatCurrency(value: number | string | null, currency: string): string {
  if (value === null) return '-'
  const numValue = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(numValue)) return '-'
  const currencyCode = currency || 'ILS'
  const locale = currencyCode === 'ILS' ? 'he-IL' : 'en-US'
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
  }).format(numValue)
}

// Format date for display
function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export function DocumentRow({ document, onSelect, onDownload }: DocumentRowProps) {
  const sumitStatus = (document.sumit_status ??
    document.extracted_metadata?.sumit_status) as SumitStatus | null | undefined
  const sumitUrl = getSumitDocumentUrl(document)

  return (
    <tr
      className="hover:bg-gray-50 cursor-pointer"
      onClick={onSelect}
      data-testid={`document-row-${document.id}`}
    >
      <td className="px-4 py-3 text-sm text-gray-900 truncate">
        {formatDate(document.document_date || document.created_at)}
      </td>
      <td className="px-4 py-3 text-sm text-gray-900 truncate">
        {document.document_number || '-'}
      </td>
      <td className="px-4 py-3 text-sm text-gray-900 truncate">{document.issued_by || '-'}</td>
      <td className="px-4 py-3 text-sm text-gray-900 truncate">
        {formatCurrency(document.value, document.original_currency)}
      </td>
      <td className="px-4 py-3 text-sm text-gray-500 truncate">
        {getDocumentTypeLabel(document.document_type, document.document_type_id)}
      </td>
      <td className="px-4 py-3">
        <StatusBadge status={document.document_status} />
      </td>
      <td className="px-4 py-3">
        {sumitUrl ? (
          <a
            href={sumitUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 hover:opacity-80"
            title="Open this document in Sumit"
            data-testid={`sumit-link-${document.id}`}
          >
            <SumitStatusBadge status={sumitStatus} />
            <span aria-hidden className="text-blue-600 text-xs">↗</span>
          </a>
        ) : (
          <SumitStatusBadge status={sumitStatus} />
        )}
      </td>
      <td className="px-4 py-3">
        <button
          onClick={(e) => {
            e.stopPropagation()
            onDownload()
          }}
          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          data-testid={`download-btn-${document.id}`}
        >
          Download
        </button>
      </td>
    </tr>
  )
}
